// Subdomæner (brands): én virksomhed kan sælge under flere navne – fx SteamGreen med SteamFox, SteamCleaner og
// Bilplejebilrens. Hvert brand har sit eget domæne, logo, farver, skrift, tekster, afsender og udvalg af produkter.
// Bookingerne lander i den samme kalender og de samme bookinger, men er mærket med brandet, så kunden får mails og
// SMS i brandets navn, og automatiseringerne kan målrettes et brand. Bruges ens i browser og på serveren.
import { fejl } from './fejl.ts';
import type { KlientBrand } from './klient.ts';
import { normBetaling } from './betaling.ts';
import { normDomaener } from './indlejring.ts';

export type SubBrand = {
  id: string;                 // kort navn i adresser og koder, fx "steamfox"
  navn: string;               // fx "SteamFox"
  domaene: string;            // hjemmesiden, fx "steamfox.dk"
  aktiv: boolean;
  sortering: number;
  design: KlientBrand;        // logo, farver og skrift (bookingsiden, mails)
  kontakt: { telefon: string; email: string; cvr: string; adresse: string; trustpilot: string; handelsbetingelser: string;
    mobilepay?: string; viva?: string; telefontid?: string };   // betaling efter opgaven (tom = virksomhedens – core/betaling.ts)
  afsender: { sms: string; mail: string };   // SMS-afsender (højst 11 tegn) og mailens afsendernavn
  tekster: { overskrift: string; undertekst: string; hilsen: string };   // bookingsiden og mails ("Venlig hilsen …")
  ydelser: string[];          // produkter, der kan bestilles under brandet (tom = alle online)
  indlejring?: string[];      // flere hjemmesider (ud over domaene), der må vise bookingen – core/indlejring.ts
};

export const TOM_BRAND = (navn = '', design?: KlientBrand): SubBrand => ({
  id: '', navn, domaene: '', aktiv: true, sortering: 0,
  design: design ?? { logo: null, primaer: '#2C473D', accent: '#E8A33C', baggrund: '#F7F5F0', tekst: '#1F2B26', font: 'Inter' },
  kontakt: { telefon: '', email: '', cvr: '', adresse: '', trustpilot: '', handelsbetingelser: '', mobilepay: '', viva: '', telefontid: '' },
  afsender: { sms: '', mail: '' },
  tekster: { overskrift: '', undertekst: '', hilsen: '' },
  ydelser: [],
  indlejring: [],
});

const HEX = /^#[0-9A-Fa-f]{6}$/;
const r = (s: unknown, n: number) => String(s ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, n);

/** Navn → kort id ("Bilplejebilrens" → "bilplejebilrens"). */
export function brandId(navn: string): string {
  return navn.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

/** Tjek og ryd et brand op før det gemmes. Kaster en fejl med de felter, der er galt. */
export function normBrand(b: SubBrand, ydelser: string[] = []): SubBrand {
  const f: Record<string, string> = {};
  const navn = r(b.navn, 80); if (navn.length < 2) f.navn = 'Skriv brandets navn.';
  const id = r(b.id, 40).toLowerCase() || brandId(navn);
  if (!/^[a-z0-9][a-z0-9-]{1,39}$/.test(id)) f.id = 'Kun små bogstaver, tal og bindestreg (2–40 tegn).';
  const domaene = r(b.domaene, 120).toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
  if (domaene && !/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domaene)) f.domaene = 'Skriv domænet, fx steamfox.dk.';
  for (const k of ['primaer', 'accent', 'baggrund', 'tekst'] as const) if (!HEX.test(b.design?.[k] ?? '')) f[k] = 'Skriv en farve som #RRGGBB.';
  if (b.design?.logo && !/^(data:image\/(png|jpeg|webp|svg\+xml)[;,]|https:\/\/)/.test(b.design.logo)) f.logo = 'Logoet skal være et billede.';
  if (b.design?.logo && b.design.logo.length > 400_000) f.logo = 'Logoet er for stort (højst ca. 300 KB).';
  const sms = r(b.afsender?.sms, 40);
  if (sms && !/^[A-Za-z0-9 ]{2,11}$/.test(sms)) f.sms = 'SMS-afsender: 2–11 bogstaver eller tal (ingen æ, ø, å).';
  const email = r(b.kontakt?.email, 254).toLowerCase();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) f.email = 'Ugyldig e-mail.';
  const url = (s: string, felt: string) => { const u = r(s, 300); if (u && !/^https:\/\/[^\s]+$/.test(u)) f[felt] = 'Skriv en adresse, der starter med https://'; return u; };
  let bet = { mobilepay: '', vivaLink: '', telefontid: '' };
  try { bet = normBetaling({ mobilepay: b.kontakt?.mobilepay, vivaLink: b.kontakt?.viva, telefontid: b.kontakt?.telefontid }); }
  catch (e) { const x = (e as { felter?: Record<string, string> }).felter ?? {}; Object.assign(f, x.mobilepay ? { mobilepay: x.mobilepay } : {}, x.vivaLink ? { viva: x.vivaLink } : {}); }
  const indl = normDomaener(b.indlejring ?? []);
  if (indl.fejl) f.indlejring = indl.fejl;
  if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f);
  return {
    id, navn, domaene, aktiv: b.aktiv !== false, sortering: Math.max(0, Math.min(999, Math.round(Number(b.sortering) || 0))),
    design: { logo: b.design.logo ?? null, primaer: b.design.primaer.toUpperCase(), accent: b.design.accent.toUpperCase(), baggrund: b.design.baggrund.toUpperCase(),
      tekst: b.design.tekst.toUpperCase(), font: r(b.design.font, 40) || 'Inter' },
    kontakt: { telefon: r(b.kontakt?.telefon, 30), email, cvr: r(b.kontakt?.cvr, 20), adresse: r(b.kontakt?.adresse, 150),
      trustpilot: url(b.kontakt?.trustpilot ?? '', 'trustpilot'), handelsbetingelser: url(b.kontakt?.handelsbetingelser ?? '', 'handelsbetingelser'),
      mobilepay: bet.mobilepay, viva: bet.vivaLink, telefontid: bet.telefontid },
    afsender: { sms, mail: r(b.afsender?.mail, 80) },
    tekster: { overskrift: r(b.tekster?.overskrift, 120), undertekst: r(b.tekster?.undertekst, 300), hilsen: r(b.tekster?.hilsen, 120) },
    ydelser: [...new Set((b.ydelser ?? []).map(String))].filter((y) => !ydelser.length || ydelser.includes(y)).slice(0, 200),
    indlejring: indl.domaener.filter((d) => d !== domaene),
  };
}

/** Firmaoplysninger i beskeder: brandets egne, ellers virksomhedens. */
export function afsenderFor(brand: SubBrand | null | undefined, firma: { navn: string; telefon: string }): { firma: string; telefon: string; sms: string } {
  if (!brand) return { firma: firma.navn, telefon: firma.telefon, sms: '' };
  return { firma: brand.navn, telefon: brand.kontakt.telefon || firma.telefon, sms: brand.afsender.sms };
}

/** Må ydelsen bestilles under brandet? (Tom liste = alle.) */
export const brandHarYdelse = (b: SubBrand | null | undefined, ydelse_id: string) => !b || !b.ydelser.length || b.ydelser.includes(ydelse_id);

/** Produkter med et brands id som forstavelse (fx "steamfox-komplet-guld") hører kun til det brand. */
export const egetProdukt = (brands: Pick<SubBrand, 'id'>[], ydelse_id: string) => brands.find((b) => ydelse_id.startsWith(b.id + '-'))?.id ?? null;

/**
 * Bookingsidens katalog: under et brand dets navn, kontakt, Trustpilot, look og produkter; uden brand (virksomhedens egen
 * side) vises ingen af brandenes egne produkter. brands = alle virksomhedens subdomæner.
 */
export function katalogForBrand<K extends {
  ydelser: { id: string }[]; firma: { navn: string; telefon: string; email: string; cvr: string; adresse: string; handelsbetingelser?: string; privatliv?: string };
  trustpilot: { url: string; vis?: boolean };
}>(k: K, b: SubBrand | null | undefined, brands: Pick<SubBrand, 'id'>[] = []): K & { brand: Pick<SubBrand, 'id' | 'navn' | 'design' | 'tekster'> | null } {
  if (!b || !b.aktiv) return { ...k, ydelser: k.ydelser.filter((y) => !egetProdukt(brands, y.id)), brand: null };
  return {
    ...k,
    ydelser: k.ydelser.filter((y) => { const ejer = egetProdukt(brands, y.id); return (!ejer || ejer === b.id) && brandHarYdelse(b, y.id); }),
    firma: { ...k.firma, navn: b.navn, telefon: b.kontakt.telefon || k.firma.telefon, email: b.kontakt.email || k.firma.email,
      cvr: b.kontakt.cvr || k.firma.cvr, adresse: b.kontakt.adresse || k.firma.adresse, handelsbetingelser: b.kontakt.handelsbetingelser || k.firma.handelsbetingelser || '' },
    // Virksomhedens Trustpilot-score og udtalelse hører ikke til brandet: kun brandets egen side som link
    trustpilot: { ...k.trustpilot, url: b.kontakt.trustpilot || k.trustpilot.url, vis: false },
    brand: { id: b.id, navn: b.navn, design: b.design, tekster: b.tekster },
  };
}

/** Tjek en bestilling under et brand: brandet skal findes og være aktivt, og produkterne skal høre til det. */
export function tjekBrandBestilling(brands: SubBrand[], id: string | null | undefined, ydelser: string[]): string | null {
  const b = id ? brands.find((x) => x.id === id && x.aktiv) : null;
  if (id && !b) throw fejl('Bookingsiden er ikke sat rigtigt op. Ring til os, så hjælper vi.');
  for (const y of ydelser) {
    const ejer = egetProdukt(brands, y);
    if ((ejer && ejer !== b?.id) || (b && !brandHarYdelse(b, y))) throw fejl('En af behandlingerne kan ikke bestilles her. Vælg en anden.');
  }
  return b?.id ?? null;
}
